import React, { useState, useCallback } from 'react';
import ReactGA4 from 'react-ga4';
import { useTranslation } from 'react-i18next';

import { SearchSelect, SelectOption, Loading, LabelWithIcon, TokensIcons } from 'components';
import { request, debounce } from 'utils';

import style from './index.module.css';

export type Token = {
  chain: string;
  decimals: number;
  display_symbol: string;
  id: string;
  is_core: boolean;
  is_verified: boolean;
  is_wallet: boolean;
  logo_url: string;
  name: string;
  optimized_symbol: string;
  price: number;
  protocol_id: string;
  symbol: string;
  time_at: number;
};

const searchToken = (q: string, chain: string) =>
  request.get<Array<Token>>('/history/token_search', { chain, q });

type FilterProps = {
  onChange: (val: Token | null) => void;
  chain: string;
  defaultValue?: string;
  value?: {
    label: string;
    value: { id: string };
  } | null;
};

export default ({ chain, onChange, value }: FilterProps) => {
  const { t } = useTranslation();
  const [options, setOptions] = useState<Array<{ label?: string; value?: any; children?: any }>>(
    [],
  );
  const [searching, setSearching] = useState(true);

  const handleSearch = useCallback(
    q => {
      searchToken(q, chain)
        .then(res => {
          const resArr = res.map(x => ({
            label: x.optimized_symbol,
            value: x,
          }));
          setOptions(resArr);
        })
        .finally(() => setSearching(false));
    },
    [setOptions, chain],
  );

  const debouceSearch = debounce(q => {
    if (!q) {
      onChange(null);
      return;
    }

    setSearching(true);
    handleSearch(q);
  }, 300);

  const formatSelectLabel = useCallback(
    (val: SelectOption) => {
      return t('History.filterHolder') + val.label;
    },
    [t],
  );

  const handleSelect = useCallback(
    (val: SelectOption) => {
      if (val) {
        ReactGA4.event({
          category: 'Search',
          action: 'token search',
          label: `${val?.label}`,
        });
      }

      onChange(val?.value);
    },
    [onChange],
  );

  return (
    <div className={style.header}>
      <SearchSelect
        value={value}
        inputClassName={style.filter}
        allowClear
        disabled={!chain}
        toolTip={!chain ? t('History.filterTip') : null}
        placeholder={t('History.filterHolder') + 'All'}
        options={options}
        onSearch={debouceSearch}
        onChange={handleSelect}
        autoClearSearchValue={false}
        formatSelectLabel={formatSelectLabel}
        notFoundContent={searching ? <Loading /> : undefined}
        renderOption={({ value }: { value: Token }) => (
          <LabelWithIcon
            icon={<TokensIcons width={24} icons={value.logo_url} />}
            label={value.optimized_symbol}
          />
        )}
      />
    </div>
  );
};
